import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Bike, CheckCircle2, ChefHat, Clock, Home, MessageCircle, PackageCheck, XCircle } from 'lucide-react';
import { useStore } from '../context/StoreContext';
import { DeliveryOrder, DeliveryOrderStatus } from '../types';
import { formatCurrency } from '../services/dataService';
import { api } from '../services/api';

const steps: { status: DeliveryOrderStatus; label: string; icon: React.ElementType }[] = [
  { status: 'RECEIVED', label: 'Pedido recebido', icon: Clock },
  { status: 'ACCEPTED', label: 'Pedido aceito', icon: CheckCircle2 },
  { status: 'PREPARING', label: 'Preparando', icon: ChefHat },
  { status: 'READY', label: 'Pronto para despacho', icon: PackageCheck },
  { status: 'OUT_FOR_DELIVERY', label: 'Saiu para entrega', icon: Bike },
  { status: 'DELIVERED', label: 'Entregue', icon: CheckCircle2 },
];

const getStepIndex = (status: DeliveryOrderStatus) => {
  return Math.max(0, steps.findIndex(step => step.status === status));
};

export const DeliveryTrack = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const publicToken = searchParams.get('token') || undefined;
  const { deliveryOrders } = useStore();
  const [serverOrder, setServerOrder] = useState<DeliveryOrder | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const localOrder = deliveryOrders.find(item => item.id === orderId);
  const order = serverOrder || localOrder;

  useEffect(() => {
    if (!orderId) return;

    let isMounted = true;
    setIsLoading(true);

    api.deliveryOrders.getPublicById(orderId, publicToken).then(foundOrder => {
      if (isMounted && foundOrder) setServerOrder(foundOrder);
    }).finally(() => {
      if (isMounted) setIsLoading(false);
    });

    const interval = window.setInterval(() => {
      api.deliveryOrders.getPublicById(orderId, publicToken).then(foundOrder => {
        if (isMounted && foundOrder) setServerOrder(foundOrder);
      });
    }, 8000);

    return () => {
      isMounted = false;
      window.clearInterval(interval);
    };
  }, [orderId, publicToken]);

  if (isLoading && !order) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 text-center max-w-md">
          <PackageCheck className="w-14 h-14 text-slate-300 mx-auto mb-4 animate-pulse" />
          <h1 className="text-2xl font-black text-slate-900">Carregando pedido</h1>
          <p className="text-sm text-slate-500 mt-2">Estamos buscando o status atualizado.</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 text-center max-w-md">
          <PackageCheck className="w-14 h-14 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-black text-slate-900">Pedido não encontrado</h1>
          <p className="text-sm text-slate-500 mt-2">Confira se o link está correto ou fale com o restaurante.</p>
          <Link to="/delivery/menu" className="inline-flex mt-5 px-4 py-3 rounded-2xl bg-slate-950 text-white text-sm font-black">Voltar ao cardápio</Link>
        </div>
      </div>
    );
  }

  const currentIndex = getStepIndex(order.status);
  const isCanceled = order.status === 'CANCELED';

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="bg-gradient-to-br from-slate-950 via-slate-900 to-brand-700 text-white px-4 py-6 rounded-b-[2rem] shadow-xl">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-black text-white/50 uppercase tracking-widest">Acompanhar pedido</p>
          <h1 className="text-2xl font-black mt-1">{order.companyName}</h1>
          <p className="text-sm text-white/60 mt-1">Pedido {order.id}</p>
          <div className="mt-4 rounded-2xl bg-white/10 border border-white/10 p-3 flex items-start gap-3">
            <MessageCircle className="w-5 h-5 text-emerald-300 mt-0.5" />
            <p className="text-xs font-bold text-white/75">Quando a API oficial do WhatsApp estiver conectada, os avisos de status serão enviados automaticamente para o número informado.</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 pb-10 space-y-4">
        <section className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-5">
          {isCanceled ? (
            <div className="text-center py-5">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-3" />
              <h2 className="text-2xl font-black text-slate-900">Pedido cancelado</h2>
              <p className="text-sm text-slate-500 mt-1">Fale com o restaurante se precisar de ajuda.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isDone = index <= currentIndex;
                const isCurrent = index === currentIndex;
                return (
                  <div key={step.status} className={`flex items-center gap-4 rounded-3xl p-4 border ${isCurrent ? 'bg-brand-50 border-brand-100' : 'bg-slate-50 border-slate-100'}`}>
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isDone ? 'bg-brand-600 text-white' : 'bg-slate-200 text-slate-400'}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <p className={`font-black ${isDone ? 'text-slate-900' : 'text-slate-400'}`}>{step.label}</p>
                      {isCurrent && <p className="text-xs font-bold text-brand-600">Status atual</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-5">
          <h2 className="font-black text-lg mb-3">Resumo</h2>
          <div className="space-y-2">
            {order.items.map(item => (
              <div key={item.id} className="flex justify-between gap-3 text-sm">
                <span className="font-bold">{item.quantity}x {item.productName}</span>
                <span className="font-black">{formatCurrency(item.price * item.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100 space-y-1">
            <div className="flex justify-between text-sm"><span>Subtotal</span><strong>{formatCurrency(order.subtotal)}</strong></div>
            <div className="flex justify-between text-sm"><span>Taxa entrega</span><strong>{order.deliveryFee === 0 ? 'A combinar' : formatCurrency(order.deliveryFee)}</strong></div>
            <div className="flex justify-between text-xl font-black"><span>Total</span><span>{formatCurrency(order.total)}</span></div>
          </div>
        </section>

        <Link to={`/loja/${order.companyId}`} className="w-full h-14 rounded-2xl bg-white border border-slate-200 text-slate-700 font-black flex items-center justify-center gap-2">
          <Home className="w-5 h-5" /> Fazer outro pedido
        </Link>
      </main>
    </div>
  );
};
